import type { ReactElement } from 'react';
import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { Info } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { StatItem } from '@/design/StatBand';

interface TopicSummaryPanelProps {
    stats: StatItem[];
}

/** The drill-in side rail's summary card: the topic's headline stats stacked vertically (the side-rail counterpart of StatBand). */
export default function TopicSummaryPanel({ stats }: TopicSummaryPanelProps): ReactElement {
    return (
        <SurfaceCard $padding="md">
            <Group gap={8} align="center" mb="xs" wrap="nowrap">
                <Box style={{ width: 3, height: 12, borderRadius: 2, background: 'var(--bm-color-brand-accent)', flexShrink: 0 }} />
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.08em' }}>
                    Summary
                </Text>
            </Group>
            <Stack gap={0}>
                {stats.map((stat, index) => (
                    <Group
                        key={stat.label}
                        justify="space-between"
                        align="center"
                        wrap="nowrap"
                        gap="sm"
                        py={8}
                        style={{ borderTop: index === 0 ? undefined : '1px solid var(--bm-color-border)' }}
                    >
                        <Group gap={4} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
                            <Text
                                size="xs"
                                c="dimmed"
                                fw={700}
                                tt="uppercase"
                                truncate
                                style={{ fontSize: '9px', letterSpacing: '0.08em' }}
                            >
                                {stat.label}
                            </Text>
                            {stat.info !== undefined && (
                                <Tooltip label={stat.info} multiline w={220} withArrow position="top">
                                    <Box
                                        component="span"
                                        aria-label={`About ${stat.label}`}
                                        style={{ display: 'inline-flex', cursor: 'help', color: 'var(--bm-color-text-dimmed)', flexShrink: 0 }}
                                    >
                                        <Info size={11} />
                                    </Box>
                                </Tooltip>
                            )}
                        </Group>
                        <Text
                            fw={700}
                            ff="monospace"
                            style={{ fontSize: '18px', letterSpacing: '-0.02em', color: 'var(--bm-color-brand-accent)', flexShrink: 0 }}
                        >
                            {stat.value}
                        </Text>
                    </Group>
                ))}
            </Stack>
        </SurfaceCard>
    );
}
